import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import ColisportApiResource from './colisport-api-resource.model';
import { DeliveryPrice } from '../../models/offer-option.model';
import { OneWayRequest } from '../../models/oneway-offer-request.model';
import { FilterShipment } from '../../models/filter-shipment.model';
import UserType from '../../models/UserType.model';
import Partner from '../../models/partner.model';
import { PaginatedResult } from '../../models/paginated.model';
import { CreatePartnerDto } from '../../models/oneway-partner-registration.model';
import {
  AdminMonthReport,
  PartnerMonthReport,
  PartnerMonthSummary,
  SignedUrl,
} from '../../models/partner-report.model';
import { CroquiStatus } from '../../models/croqui-status.model';
import { getToken } from './auth-session-storage.service';

export default class ColisportBikeApi implements ColisportApiResource {
  readonly apiUrlPath = 'bike';

  constructor(readonly httpClient: AxiosInstance) {}

  async getAvailablePartners(
    isElectricBike: boolean,
    isTandemBike: boolean,
  ): Promise<AxiosResponse<Partner[]>> {
    return this.httpClient.get(
      `${this.apiUrlPath}/partner/${isElectricBike.toString()}/${String(isTandemBike)}`,
    );
  }

  async getAvailablePartnersWithFilter(
    filter: string,
    isElectricBike: boolean,
    isTandemBike: boolean,
    // Config axios OPCIONAL (aditivo, retrocompatível). O palco passa um
    // AbortSignal via useDebouncedResource para cancelar buscas obsoletas.
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Partner[]>> {
    return this.httpClient.get(
      `${this.apiUrlPath}/partner/with-filter/${isElectricBike}/${isTandemBike}/${filter}`,
      config,
    );
  }

  /**
   * Busca PAGINADA de lojas (palco /colibike, scroll infinito). Query params:
   * isElectric/isTandem (SEMPRE), filter (opcional - vazio = todas), page, limit.
   * Devolve o envelope PaginatedResult. O AbortSignal (via useInfiniteResource)
   * cancela páginas obsoletas.
   */
  async searchAvailablePartnersPaginated(
    params: {
      isElectric: boolean;
      isTandem: boolean;
      filter: string;
      page: number;
      limit: number;
    },
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<PaginatedResult<Partner>>> {
    return this.httpClient.get(`${this.apiUrlPath}/partner-search`, {
      ...(config ?? {}),
      params,
    });
  }

  async getAllPartners(): Promise<AxiosResponse<Partner[]>> {
    return this.httpClient.get(`${this.apiUrlPath}/allPartners`);
  }

  async getPartnersBackOffice(query?: string): Promise<AxiosResponse<any>> {
    // The query string is already embedded in the URL above. Passing a raw
    // string as `params:` (the old behavior) made axios enumerate the
    // characters of the string into per-character query keys
    // (`?0=f&1=i&2=l&...`), which corrupted every request.
    return this.httpClient.get(
      `${this.apiUrlPath}/partners/filters${query !== '' ? `?${query}` : ''}`,
    );
  }

  async addPartner(data: CreatePartnerDto): Promise<AxiosResponse<CreatePartnerDto>> {
    return this.httpClient.post(`${this.apiUrlPath}/partner`, data);
  }

  /** B2 (doc 16): recover de password de partner - o backend devolve SEMPRE 200. */
  async recoverPartnerPassword(email: string): Promise<AxiosResponse<Record<string, never>>> {
    return this.httpClient.post(`${this.apiUrlPath}/partner/recover`, { email });
  }

  /** 10b: refresh silencioso da sessão de partner (JWT válido → token novo). */
  async refreshPartnerSession(): Promise<AxiosResponse<{ accessToken: string }>> {
    return this.httpClient.post(`${this.apiUrlPath}/partner/refresh-session`, {});
  }

  /** B2 (doc 16): reset com o token do email (1h, uso único). */
  /** Disponibilité (doc 19 D): timeline dos próximos dias do próprio partner. */
  async getPartnerAvailability(
    days: number,
  ): Promise<AxiosResponse<{ from: string; days: Array<{ date: string; state: string }> }>> {
    return this.httpClient.get(`${this.apiUrlPath}/partner/availability`, { params: { days } });
  }

  /** Disponibilité (doc 19 D): feriados franceses do ano (calendrier.api.gouv). */
  async getPartnerHolidays(
    year: number,
  ): Promise<AxiosResponse<Array<{ date: string; label: string }>>> {
    return this.httpClient.get(`${this.apiUrlPath}/partner/holidays`, { params: { year } });
  }

  /** Mon profil · Sécurité: o próprio partner (JWT) muda a password sabendo a atual. */
  async changePartnerPassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<AxiosResponse<Record<string, never>>> {
    return this.httpClient.post(`${this.apiUrlPath}/partner/change-password`, {
      currentPassword,
      newPassword,
    });
  }

  async resetPartnerPassword(
    token: string,
    password: string,
  ): Promise<AxiosResponse<Record<string, never>>> {
    return this.httpClient.post(`${this.apiUrlPath}/partner/reset`, { token, password });
  }

  async updatePartner(data: Partial<Partner>, email: string): Promise<AxiosResponse<Partner>> {
    return this.httpClient.patch(`${this.apiUrlPath}/partner/${email}`, data);
  }

  /**
   * B2.1 (aditivo): perfil do partner autenticado via o httpClient PARTILHADO -
   * o interceptor anexa o Bearer do sessionStorage a cada request, por isso
   * sobrevive a F5 (ao contrário do axios global do user.service, que depende
   * do default posto no login). É o caminho canónico do guard do /colibike/.
   */
  async getProfile(): Promise<AxiosResponse<Partner>> {
    return this.httpClient.get(`${this.apiUrlPath}/partner/profile`);
  }

  /**
   * B2.2 (aditivo): lista "À traiter" do partner autenticado - endpoint novo
   * GET /bike/shipments/todo (B1.6). Paginação REAL no servidor; cada item
   * traz `side` ('shipper'|'receiver') e `task` derivadas no backend, e a
   * ordem é a do servidor (dueAt do lado que faz match, asc, nulls no fim).
   */
  async getTodoShipments(page: number, limit: number): Promise<AxiosResponse<FilterShipment>> {
    return this.httpClient.get(
      `${this.apiUrlPath}/shipments/todo?page=${page}&limit=${limit}`,
    );
  }

  /**
   * B2.4 (aditivo): check-in/check-out POR BIKE numa chamada (B1.2 - croqui +
   * flag atómico condicionado ao estado). 400 WRONG_FILE_TYPE/FILE_TOO_LARGE,
   * 409 INVALID_STATE, 200 idempotente com counts {checked,total}.
   */
  async checkBikePackage(
    colisportID: string,
    packageIndex: number,
    kind: 'check-in' | 'check-out',
    file: string,
  ): Promise<AxiosResponse<{ counts: { checked: number; total: number } }>> {
    return this.httpClient.post(
      `${this.apiUrlPath}/shipments/${colisportID}/packages/${packageIndex}/${kind}`,
      { file },
    );
  }

  /**
   * B2.5 (aditivo): entrega ao cliente com o código verificado NO SERVIDOR
   * (B1.4). 403 genérico = código errado; 403 CODE_LOCKED após 10 tentativas;
   * 409 INVALID_STATE; 200 idempotente.
   */
  async validateOrderDeliveredWithCode(
    colisportID: string,
    code: string,
  ): Promise<AxiosResponse<void>> {
    return this.httpClient.put(
      `${this.apiUrlPath}/validate-order-delivered-to-user/${colisportID}`,
      { code },
    );
  }

  /**
   * B2.6 (aditivo): bordereau do lado shipper (B1.5) - mesmo ficheiro que o
   * endpoint antigo de printing-labels, com posse + carimbo 1ª vez. 404
   * enquanto não houver labels.
   */
  async downloadBordereau(colisportID: string): Promise<AxiosResponse<ArrayBuffer>> {
    return this.httpClient.get(`${this.apiUrlPath}/shipments/${colisportID}/bordereau`, {
      responseType: 'arraybuffer',
    });
  }

  async getOffers(data: OneWayRequest): Promise<AxiosResponse<DeliveryPrice[]>> {
    return this.httpClient.post(`${this.apiUrlPath}/offers`, data);
  }

  async getShipments(query?: string): Promise<AxiosResponse<FilterShipment>> {
    // The query string is already embedded in the URL. Passing a raw string
    // as `params:` (the old behavior) caused axios to enumerate string
    // indices into per-character query keys, e.g.
    // `&0=shipments?limit=50&...`, which corrupted every call.
    return this.httpClient.get(`${this.apiUrlPath}/shipments${query !== '' ? `?${query}` : ''}`);
  }

  /** Doc 24 (back-office /colibike/): estado do croqui + URL pré-assinado
   *  (5 min) quando 'ok'; 'expired' é um estado, não um erro. */
  async getCroquiStatus(
    colisportID: string,
    index: number,
    croquiType: 'check-in' | 'check-out',
  ): Promise<AxiosResponse<CroquiStatus>> {
    return this.httpClient.get(
      `${this.apiUrlPath}/shipments/${colisportID}/croquis/${index}/${croquiType}`,
      this.auth(),
    );
  }

  async validatePackages(
    colisportID: string,
    onewayStoreType: UserType,
  ): Promise<AxiosResponse<{ codeEmailSent?: boolean }>> {
    // B1.3 (achado #1): a resposta traz `codeEmailSent` no lado receiver.
    return this.httpClient.put(
      `${this.apiUrlPath}/validate-packages/${colisportID}/${onewayStoreType}`,
    );
  }

  /* ── Mes rapports (docs 20/21): partner só o SEU (identidade do token) ── */

  async getPartnerReport(month: string): Promise<AxiosResponse<PartnerMonthReport>> {
    return this.httpClient.get(`${this.apiUrlPath}/partner/reports/${month}`, this.auth());
  }

  async getPartnerReportHistory(): Promise<AxiosResponse<PartnerMonthSummary[]>> {
    return this.httpClient.get(`${this.apiUrlPath}/partner/reports/history`, this.auth());
  }

  /** Multipart (campo `file`): o axios põe o Content-Type com o boundary. */
  async uploadPartnerInvoice(month: string, file: File): Promise<AxiosResponse<PartnerMonthReport>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.httpClient.post(`${this.apiUrlPath}/partner/reports/${month}/invoice`, form, this.auth());
  }

  async getPartnerInvoiceUrl(month: string): Promise<AxiosResponse<SignedUrl>> {
    return this.httpClient.get(`${this.apiUrlPath}/partner/reports/${month}/invoice-url`, this.auth());
  }

  /* ── admin ── */

  async getAdminReports(month: string): Promise<AxiosResponse<AdminMonthReport>> {
    return this.httpClient.get(`${this.apiUrlPath}/admin/reports/${month}`, this.auth());
  }

  async getAdminPartnerReport(
    month: string,
    partnerId: string,
  ): Promise<AxiosResponse<PartnerMonthReport>> {
    return this.httpClient.get(`${this.apiUrlPath}/admin/reports/${month}/${partnerId}`, this.auth());
  }

  async getAdminInvoiceUrl(month: string, partnerId: string): Promise<AxiosResponse<SignedUrl>> {
    return this.httpClient.get(
      `${this.apiUrlPath}/admin/reports/${month}/${partnerId}/invoice-url`,
      this.auth(),
    );
  }

  async markReportPaid(
    month: string,
    partnerId: string,
    paymentRef?: string,
  ): Promise<AxiosResponse<PartnerMonthReport>> {
    return this.httpClient.post(
      `${this.apiUrlPath}/admin/reports/${month}/${partnerId}/paid`,
      { paymentRef },
      this.auth(),
    );
  }

  private auth(): AxiosRequestConfig {
    return { headers: { Authorization: `Bearer ${getToken()}` } };
  }
}
